import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faClock,
  faHourglassHalf,
  faPaperPlane,
  faRotate,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../../../AxiosInstance";
import { toast, ToastTypes } from "../../../components/ajonjolib/toasts/toast/toast";
import { formatTimestamp, pduiIsReady, setCompanyTimezone } from "../../../utils/utils";
import styles from "../company.module.css";

// Mientras haya equipos sin confirmar, se vuelve a preguntar cada tanto.
const PENDING_REFRESH_MS = 20000;

function offsetLabel(tz) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch (e) {
    return "";
  }
}

function zoneLabel(tz) {
  const city = tz.split("/").slice(1).join(" / ").replace(/_/g, " ") || tz;
  const offset = offsetLabel(tz);
  return offset ? `${city} (${offset})` : city;
}

function nowIn(tz, locale) {
  try {
    return new Date().toLocaleString(locale, {
      timeZone: tz,
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

function deviceStatus(pdui, t) {
  if (pdui.device_timezone_error) {
    return { kind: "error", text: t("company.tz.statusError"), detail: pdui.device_timezone_error };
  }
  if (pdui.device_timezone && pdui.device_timezone === pdui.timezone) {
    return {
      kind: "ok",
      text: t("company.tz.statusOk"),
      detail: pdui.device_timezone_at
        ? t("company.tz.confirmedAt", { date: formatTimestamp(pdui.device_timezone_at) })
        : "",
    };
  }
  const online = pdui.last_heartbeat && pduiIsReady(pdui.last_heartbeat);
  let detail = online ? t("company.tz.pendingOnline") : t("company.tz.pendingOffline");
  if (pdui.device_timezone) detail = `${t("company.tz.deviceHas", { tz: pdui.device_timezone })} ${detail}`;
  return { kind: "pending", text: t("company.tz.statusPending"), detail };
}

const STATUS_STYLE = {
  ok: { className: styles.statusOk, icon: faCircleCheck },
  pending: { className: styles.statusPending, icon: faHourglassHalf },
  error: { className: styles.statusError, icon: faTriangleExclamation },
};

export default function TimezoneCard({ data, setData, countries, reload }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.substring(0, 2) || "es";
  const { company, pduis } = data;
  const [country, setCountry] = useState(company.country || "");
  const [timezone, setTimezone] = useState(company.timezone);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [, setTick] = useState(0);

  useEffect(() => {
    setCountry(company.country || "");
    setTimezone(company.timezone);
  }, [company.country, company.timezone]);

  // Reloj de vista previa.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: "region" });
    } catch (e) {
      return null;
    }
  }, [locale]);

  const countryOptions = useMemo(() => {
    const named = countries.map((c) => ({
      ...c,
      label: (regionNames && regionNames.of(c.code)) || c.name,
    }));
    named.sort((a, b) => a.label.localeCompare(b.label, locale));
    return named;
  }, [countries, regionNames, locale]);

  const zones = useMemo(() => {
    const match = countries.find((c) => c.code === country);
    if (match) return match.timezones;
    return timezone ? [timezone] : [];
  }, [countries, country, timezone]);

  const pending = pduis.filter((p) => deviceStatus(p, t).kind !== "ok").length;

  useEffect(() => {
    if (!pending) return undefined;
    const id = setInterval(reload, PENDING_REFRESH_MS);
    return () => clearInterval(id);
  }, [pending, reload]);

  const onCountryChange = (code) => {
    setCountry(code);
    setError("");
    const match = countries.find((c) => c.code === code);
    if (match && !match.timezones.includes(timezone)) setTimezone(match.timezones[0]);
  };

  const dirty = country !== (company.country || "") || timezone !== company.timezone;

  const save = () => {
    setSaving(true);
    setError("");
    axiosInstance
      .patch("company/settings/", { country, timezone })
      .then((response) => {
        setData(response.data);
        setCompanyTimezone(response.data.company.timezone);
        toast(t("company.tz.saved", { count: response.data.pduis.length }), ToastTypes.SUCCESS);
      })
      .catch((err) => {
        const body = err?.response?.data || {};
        setError((body.timezone && body.timezone[0]) || (body.country && body.country[0]) || t("company.saveError"));
      })
      .finally(() => setSaving(false));
  };

  // Los equipos que ya existían antes de esta función nunca la recibieron, y
  // uno reinstalado la pierde: se puede volver a mandar sin cambiarla.
  const resend = () => {
    setResending(true);
    axiosInstance
      .post("company/settings/resend-timezone/")
      .then((response) => {
        setData(response.data);
        toast(t("company.tz.resent", { count: response.data.pduis.length }), ToastTypes.SUCCESS);
      })
      .catch(() => toast(t("company.saveError"), ToastTypes.ERROR))
      .finally(() => setResending(false));
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>
            <FontAwesomeIcon icon={faClock} className={styles.cardTitleIcon} />
            {t("company.tz.title")}
          </div>
          <div className={styles.cardHelp}>{t("company.tz.help")}</div>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label htmlFor="company-country">{t("company.tz.country")}</label>
          <select
            id="company-country"
            className={styles.select}
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
          >
            {!country && <option value="">{t("company.tz.chooseCountry")}</option>}
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor="company-timezone">{t("company.tz.zone")}</label>
          <select
            id="company-timezone"
            className={styles.select}
            value={timezone}
            disabled={zones.length <= 1}
            onChange={(e) => {
              setTimezone(e.target.value);
              setError("");
            }}
          >
            {zones.map((tz) => (
              <option key={tz} value={tz}>
                {zoneLabel(tz)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {error && <div className={styles.fieldError} style={{ marginTop: 8 }}>{error}</div>}

      <div className={styles.clockPreview}>
        <FontAwesomeIcon icon={faClock} />
        <div>
          {t("company.tz.nowThere")} <strong>{nowIn(timezone, locale)}</strong>
        </div>
      </div>

      <div className={styles.actions}>
        {dirty && (
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => {
              setCountry(company.country || "");
              setTimezone(company.timezone);
              setError("");
            }}
          >
            {t("company.cancel")}
          </button>
        )}
        <button type="button" className={styles.primaryButton} disabled={!dirty || saving} onClick={save}>
          {saving ? t("company.saving") : t("company.tz.save")}
        </button>
      </div>

      <div className={styles.sectionLabel}>
        {t("company.tz.devices", { count: pduis.length })}
        {pending > 0 && ` · ${t("company.tz.pendingCount", { count: pending })}`}
      </div>
      <div className={styles.list}>
        <div className={`${styles.listHeader} ${styles.devicesGrid}`}>
          <div>{t("company.tz.device")}</div>
          <div>{t("company.tz.zoneOnDevice")}</div>
          <div>{t("company.tz.status")}</div>
        </div>
        {pduis.length === 0 && <div className={styles.emptyRow}>{t("company.tz.noDevices")}</div>}
        {pduis.map((p) => {
          const status = deviceStatus(p, t);
          const look = STATUS_STYLE[status.kind];
          return (
            <div key={p.id} className={`${styles.listRow} ${styles.devicesGrid}`}>
              <div style={{ minWidth: 0 }}>
                <div className={styles.personName}>{p.name}</div>
                <div className={styles.deviceRef}>{t("company.tz.reference", { ref: p.reference })}</div>
              </div>
              <div className={styles.personEmail} style={{ fontSize: 13 }}>
                {p.device_timezone || "—"}
              </div>
              <div style={{ minWidth: 0 }}>
                <span className={`${styles.statusPill} ${look.className}`}>
                  <FontAwesomeIcon icon={look.icon} />
                  {status.text}
                </span>
                {status.detail && <div className={styles.statusDetail}>{status.detail}</div>}
              </div>
            </div>
          );
        })}
      </div>
      {pduis.length > 0 && (
        <div className={styles.actions} style={{ marginTop: 10 }}>
          <button type="button" className={styles.ghostButton} onClick={reload}>
            <FontAwesomeIcon icon={faRotate} />
            {t("company.refresh")}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={resend}
            disabled={resending || dirty}
            title={dirty ? t("company.tz.resendDirty") : t("company.tz.resendHelp")}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {resending ? t("company.saving") : t("company.tz.resend")}
          </button>
        </div>
      )}
    </div>
  );
}
